import { AnyAction, createSlice, Dispatch, Middleware, PayloadAction } from '@reduxjs/toolkit'
import { AuthCookieService } from '@utils/AuthCookieService'
import { AppDispatch } from '../store'

export interface AuthState {
  accessToken?: string
  refreshToken?: string
  status: {
    signIn?: 'loading' | 'success' | 'failed'
    signUp?: 'loading' | 'success' | 'failed'
  }
}

const initialState: AuthState = {
  accessToken: undefined,
  refreshToken: undefined,
  status: {
    signIn: undefined,
    signUp: undefined,
  },
}

export type SignInSuccessType = {
  accessToken: string
  refreshToken: string
}

export type SignUpSuccessType = {
  accessToken: string
  refreshToken: string
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initializeAuthState: (state, action: PayloadAction<{ accessToken?: string }>) => {
      state.accessToken = action.payload.accessToken
      state.status.signIn = action.payload.accessToken ? 'success' : undefined
    },
    signUpLoading: (state) => {
      state.status.signUp = 'loading'
    },
    signUpError: (state) => {
      state.status.signUp = 'failed'
    },
    signUpSuccess: (state, action: PayloadAction<SignUpSuccessType>) => {
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
      state.status.signUp = 'success'
      state.status.signIn = 'success'
    },
    signInLoading: (state) => {
      state.status.signIn = 'loading'
    },
    signInError: (state) => {
      state.status.signIn = 'failed'
    },
    signInSuccess: (state, action: PayloadAction<SignInSuccessType>) => {
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
      state.status.signIn = 'success'
    },
    signOut: (state) => {
      state.accessToken = undefined
      state.refreshToken = undefined
      state.status.signIn = undefined
      state.status.signUp = undefined
    },
  },
})

// Export actions
export const {
  initializeAuthState,
  signUpLoading,
  signUpError,
  signUpSuccess,
  signInLoading,
  signInError,
  signInSuccess,
  signOut,
} = authSlice.actions

// Create a middleware or effect to handle the side effects

export const authMiddleware: Middleware = () => (next: Dispatch<AnyAction>) => (action: AnyAction) => {
  const result = next(action)

  if (signInSuccess.match(action) || signUpSuccess.match(action)) {
    // Store auth info in cookie
    AuthCookieService.storeAuthInfo(
      {
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
      },
      true,
    )
  }

  if (signOut.match(action)) {
    AuthCookieService.clearAuthInfo()
  }

  return result
}

export const initializeAuth = async (dispatch: AppDispatch) => {
  try {
    const queryParams = new URLSearchParams(window.location.search)
    const accessToken = queryParams.get('accessToken')

    if (accessToken) {
      await AuthCookieService.storeAuthInfo({ accessToken }, true)
      dispatch(initializeAuthState({ accessToken }))
    } else {
      const authInfo = await AuthCookieService.getAuthInfo()
      dispatch(initializeAuthState({ accessToken: authInfo?.accessToken }))
    }
  } catch (error) {
    console.error('Error initializing auth state:', error)
  }
}

export default authSlice.reducer
